export const environmentNames = ['prod', 'sandbox', 'phitest', 'dev'] as const;
export type Env = (typeof environmentNames)[number];

export const isValidEnv = (maybeEnv: string): maybeEnv is Env =>
  environmentNames.includes(maybeEnv as never);

const envPatternMappings: Record<Env, Array<RegExp>> = {
  dev: [/^([a-z][a-z.-]*)\.dev\.(zushealth|zusapi)\.com$/i, /^preview(.*)$/i],
  phitest: [/^([a-z][a-z.-]*)\.(phitest|phi-test)\.(zushealth|zusapi)\.com$/i],
  sandbox: [/^([a-z][a-z.-]*)\.sandbox\.(zushealth|zusapi)\.com$/i],
  prod: [/^([a-z][a-z.-]*)\.(zushealth|zusapi)\.com$/i],
} as const;

interface GetEnvFromCurrentHostnameOptions {
  fallbackEnv?: Env;
}

export function getEnvFromCurrentHostname(options: GetEnvFromCurrentHostnameOptions = {}): Env {
  return getEnvFromHostname(window.location.hostname, options);
}

/*
 * Server-side variant of `getEnvFromCurrentHostname`. Prefer this over `ENV` on the server:
 * standalone's `ENV` is `production` in prod, which isn't a valid `Env`.
 */
export function getEnvFromHostname(
  hostnameWithOptionalPort: string,
  { fallbackEnv }: GetEnvFromCurrentHostnameOptions = {},
): Env {
  const hostname = hostnameWithOptionalPort.split(':')[0];

  for (const [env, patterns] of Object.entries(envPatternMappings)) {
    for (const pattern of patterns) {
      if (pattern.test(hostname) && isValidEnv(env)) {
        return env;
      }
    }
  }

  if (!fallbackEnv) {
    throw new Error(
      'Could not determine application environment from URL and no fallbackEnv was provided',
    );
  }

  return fallbackEnv;
}

export const currentHostnameIsLocalDevEnvironment = () =>
  ['localhost', '127.0.0.1'].some((host) => window.location.hostname.includes(host));

interface GetEnvFromEnvironmentVariableOptions {
  fallbackEnv?: Env;
}

export const getEnvFromEnvironmentVariable = ({
  fallbackEnv,
}: GetEnvFromEnvironmentVariableOptions = {}): Env => {
  const envFromEnvVar = // Always use the test environment's specified environment if present
    validEnvOrUndefined(process.env.PLAYWRIGHT_ISOLATED_TEST_ENV) ??
    // Fallback to the standard ENV var
    validEnvOrUndefined(process.env.ENV) ??
    validEnvOrUndefined(process.env.NEXT_PUBLIC_ENV) ??
    fallbackEnv;

  if (!envFromEnvVar) {
    throw new Error(
      'The env was not specified via environment variable and no fallbackEnv was provided',
    );
  }

  return envFromEnvVar;
};

export const envValueOrThrow = (envVarName: string): string => {
  const value = process.env[envVarName];
  if (!value) {
    throw new Error(`${envVarName} environment variable is not set`);
  }
  return value;
};

export const validEnvOrUndefined = (maybeEnv: string | undefined): Env | undefined => {
  if (maybeEnv === undefined) {
    return undefined;
  }

  const env = maybeEnv.toLowerCase();

  switch (env) {
    case 'prod':
    case 'production':
      return 'prod';
    case 'dev':
    case 'development':
      return 'dev';
    case 'phitest':
    case 'phi-test':
      return 'phitest';
    case 'sandbox':
      return 'sandbox';
    default:
      return undefined;
  }
};

export function getAuthServiceOauthTokenUrl(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://zus.us.auth0.com/oauth/token';
    case 'dev':
      return 'https://zus-dev.us.auth0.com/oauth/token';
    case 'sandbox':
      return 'https://zus-sandbox.us.auth0.com/oauth/token';
    case 'phitest':
      return 'https://zus-phi-test.us.auth0.com/oauth/token';
    default:
      return 'https://zus-dev.us.auth0.com/oauth/token';
  }
}

export function getZusApiBaseUrl(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://api.zusapi.com';
    case 'dev':
      return 'https://api.dev.zusapi.com';
    case 'sandbox':
      return 'https://api.sandbox.zusapi.com';
    case 'phitest':
      return 'https://api.phitest.zusapi.com';
    default:
      return 'https://api.dev.zusapi.com';
  }
}

/*
 * The Backend-for-frontend API base URL
 */
export function getBffApiBaseUrl(env: Env): string {
  if (typeof window === 'undefined') {
    return bffApiBaseUrlForEnv(env);
  }

  return getBffApiBaseUrlForHostname(window.location.hostname, env);
}

/*
 * Server-side variant of `getBffApiBaseUrl` for callers that have the request's host but no
 * `window` — without it they'd silently get the shared env URL in preview and local dev.
 */
export function getBffApiBaseUrlForHostname(hostname: string, env: Env): string {
  const hostnameWithoutPort = hostname.split(':')[0];

  if (hostnameWithoutPort === 'localhost') {
    return 'http://localhost:7552';
  }

  if (hostnameWithoutPort.startsWith('preview-pr-')) {
    return `https://${hostnameWithoutPort.split('.')[0]}.api.app.dev.zushealth.com`;
  }

  return bffApiBaseUrlForEnv(env);
}

function bffApiBaseUrlForEnv(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://api.app.zushealth.com';
    case 'dev':
      return 'https://api.app.dev.zushealth.com';
    case 'sandbox':
      return 'https://api.app.sandbox.zushealth.com';
    case 'phitest':
      return 'https://api.app.phitest.zushealth.com';
    default:
      return 'https://api.app.dev.zushealth.com';
  }
}

/*
 * The CDA Renderer API base URL
 */
export function getCdaRendererBaseUrl(env: Env): string {
  const windowIsLocalhost =
    typeof window !== 'undefined' && window.location.hostname === 'localhost';
  if (windowIsLocalhost) {
    return 'http://localhost:7552';
  }

  switch (env) {
    case 'prod':
      return 'https://cda-renderer-pilot.zusapi.com';
    case 'dev':
      return 'https://cda-renderer-pilot.dev.zusapi.com';
    case 'sandbox':
      return 'https://cda-renderer-pilot.sandbox.zusapi.com';
    case 'phitest':
      return 'https://cda-renderer-pilot.phitest.zusapi.com';
    default:
      return 'https://cda-renderer-pilot.dev.zusapi.com';
  }
}

type ApiService =
  | 'auth-service'
  | 'auth-thingy'
  | 'data-transformer'
  | 'ehr-hooks'
  | 'forms-data'
  | 'fhir'
  | 'fhir-async'
  | 'fqs'
  | 'lens';

const apiServicesThatUsePhitestSubdomainWithoutADash: Array<ApiService> = [
  'auth-service',
  'auth-thingy',
  'data-transformer',
  'ehr-hooks',
  'fhir',
  'fhir-async',
  'fqs',
  'lens',
];

interface GetApiServiceBaseUrlOptions {
  service: ApiService;
  env: Env;
}

export function getApiServiceBaseUrl({ env, service }: GetApiServiceBaseUrlOptions): string {
  const phiTestUrlEnv = apiServicesThatUsePhitestSubdomainWithoutADash.includes(service)
    ? 'phitest'
    : 'phi-test';

  switch (env) {
    case 'prod':
      return `https://${service}.zusapi.com`;
    case 'dev':
      return `https://${service}.dev.zusapi.com`;
    case 'sandbox':
      return `https://${service}.sandbox.zusapi.com`;
    case 'phitest':
      return `https://${service}.${phiTestUrlEnv}.zusapi.com`;
    default:
      return `https://${service}.dev.zusapi.com`;
  }
}

interface GetAIApiServiceBaseUrlOptions {
  env: Env;
}

export function getAiServiceBaseUrl({ env }: GetAIApiServiceBaseUrlOptions): string {
  switch (env) {
    case 'prod':
      return 'https://ai.zusapi.com';
    case 'dev':
      return 'https://ai.dev.zusapi.com';
    case 'sandbox':
      return 'https://ai.sandbox.zusapi.com';
    case 'phitest':
      return 'https://ai.phitest.zusapi.com';
    default:
      return 'https://ai.dev.zusapi.com';
  }
}

export function getZapBaseUrl(env: Env): string {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5173';
  }

  if (window.location.hostname.startsWith('preview-pr-')) {
    return `https://${window.location.hostname.split('.')[0]}.zap.dev.zushealth.com`;
  }

  switch (env) {
    case 'prod':
      return 'https://zap.zushealth.com';
    case 'dev':
      return 'https://zap.dev.zushealth.com';
    case 'sandbox':
      return 'https://zap.sandbox.zushealth.com';
    case 'phitest':
      return 'https://zap.phitest.zushealth.com';
    default:
      return 'https://zap.dev.zushealth.com';
  }
}

export function getUpi2ApiBaseUrl(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://upi2.zusapi.com';
    case 'dev':
      return 'https://upi2.dev.zusapi.com';
    case 'sandbox':
      return 'https://upi2.sandbox.zusapi.com';
    case 'phitest':
      return 'https://upi2.phitest.zusapi.com';
    default:
      return 'https://upi2.dev.zusapi.com';
  }
}

export function getStandaloneBaseUrl(env: Env): string {
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:3000';
  }

  if (typeof window !== 'undefined' && window.location.hostname.startsWith('preview-pr-')) {
    return `https://${window.location.hostname.split('.')[0]}.app.dev.zushealth.com`;
  }

  switch (env) {
    case 'prod':
      return 'https://app.zushealth.com';
    case 'dev':
      return 'https://app.dev.zushealth.com';
    case 'sandbox':
      return 'https://app.sandbox.zushealth.com';
    case 'phitest':
      return 'https://app.phitest.zushealth.com';
    default:
      return 'https://app.dev.zushealth.com';
  }
}
